import { Link, router } from '@inertiajs/react'
import { useEffect, useRef, useState } from 'react'
import InfoTip from '../../../components/InfoTip'

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatAt(iso) {
  if (!iso) return '—'
  return new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
}

function timeAgo(iso) {
  if (!iso) return '—'
  const sec = Math.round((Date.now() - new Date(iso).getTime()) / 1000)
  if (sec < 60)   return `${sec}s ago`
  if (sec < 3600) return `${Math.round(sec / 60)}m ago`
  if (sec < 86400)return `${Math.round(sec / 3600)}h ago`
  return `${Math.round(sec / 86400)}d ago`
}

const CARRIER_COLORS = {
  B6: '#003087', DL: '#E01933', UA: '#005DAA', AA: '#0078D2', WN: '#304CB2',
}

const SEGMENT_META = {
  airline:    { icon: '✈', label: 'Airline',     color: '#2196F3' },
  hotel:      { icon: '🏨', label: 'Hotel',       color: '#9C27B0' },
  car_rental: { icon: '🚗', label: 'Car Rental',  color: '#FF9800' },
  cruise:     { icon: '🚢', label: 'Cruise',      color: '#00BCD4' },
}

// ─── Flight mutation card ─────────────────────────────────────────────────────

function MutationCard({ mutation }) {
  const [expanded, setExpanded] = useState(false)
  const carrierColor = CARRIER_COLORS[mutation.carrier_code] || '#607D8B'
  const segMeta      = SEGMENT_META[mutation.segment_type] || SEGMENT_META.airline
  const deltaColor   = mutation.revenue_delta > 0 ? '#4CAF50' : mutation.revenue_delta < 0 ? '#F44336' : '#9E9E9E'
  const orig = mutation.original_flight
  const newFl = mutation.new_flight

  return (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden hover:border-gray-300 transition-colors">
      {/* Header row */}
      <div
        className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-gray-50"
        onClick={() => setExpanded(e => !e)}
      >
        {/* Segment badge */}
        <span
          className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-base"
          style={{ background: segMeta.color + '22' }}
        >
          {segMeta.icon}
        </span>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono font-bold text-sm text-gray-900">{mutation.pnr_locator}</span>
            <span className="font-mono font-bold text-xs" style={{ color: carrierColor }}>
              {mutation.carrier_code}
            </span>
            {mutation.cabin_class && (
              <span className="text-xs bg-gray-100 text-gray-600 px-1.5 py-0 rounded font-mono">
                {mutation.cabin_class}
              </span>
            )}
            <span
              className="text-xs px-1.5 py-0 rounded font-semibold"
              style={{ background: segMeta.color + '22', color: segMeta.color }}
            >
              {segMeta.label}
            </span>
            {mutation.itinerary_ref && (
              <a
                href={`/admin/cloudtraq_gds/itinerary/${mutation.itinerary_ref}`}
                className="text-xs text-teal-600 hover:underline font-mono"
                onClick={e => e.stopPropagation()}
              >
                {mutation.itinerary_ref}
              </a>
            )}
          </div>

          {/* Mutation summary */}
          <div className="flex items-center gap-3 mt-0.5 text-xs text-gray-500 flex-wrap">
            {orig?.flight_number && newFl?.flight_number ? (
              <>
                <span className="line-through text-gray-400">{orig.flight_number}</span>
                <span>→</span>
                <span className="font-semibold text-gray-700">{newFl.flight_number}</span>
                {newFl.departure_time && (
                  <span className="text-gray-500">{formatAt(newFl.departure_time)}</span>
                )}
              </>
            ) : (
              <span>Rebooking confirmed</span>
            )}
          </div>
        </div>

        {/* Revenue delta */}
        {mutation.revenue_delta !== 0 && mutation.revenue_delta != null && (
          <div className="flex-shrink-0 text-right">
            <p className="text-sm font-bold tabular-nums" style={{ color: deltaColor }}>
              {mutation.revenue_delta > 0 ? '+' : ''}${Math.abs(mutation.revenue_delta).toFixed(2)}
            </p>
            <p className="text-xs text-gray-400">revenue delta</p>
          </div>
        )}

        <div className="flex items-center gap-2 flex-shrink-0">
          <span className="text-xs text-gray-400">{timeAgo(mutation.rebooking_at)}</span>
          <span className="text-gray-400">{expanded ? '▲' : '▼'}</span>
        </div>
      </div>

      {/* Expanded detail */}
      {expanded && (
        <div className="border-t border-gray-100 px-4 py-3 text-xs space-y-3">

          {/* Original vs new schedule */}
          {(orig || newFl) && (
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-red-50 border border-red-200 rounded p-2.5">
                <p className="font-semibold text-red-700 mb-1.5">Original Schedule</p>
                {orig?.flight_number && <p><span className="text-gray-500">Flight:</span> <span className="font-mono">{orig.flight_number}</span></p>}
                {orig?.departure_time && <p><span className="text-gray-500">Depart:</span> {formatAt(orig.departure_time)}</p>}
                {orig?.arrival_time   && <p><span className="text-gray-500">Arrive:</span> {formatAt(orig.arrival_time)}</p>}
                {orig?.cabin_class    && <p><span className="text-gray-500">Cabin:</span>  {orig.cabin_class}</p>}
              </div>
              <div className="bg-green-50 border border-green-200 rounded p-2.5">
                <p className="font-semibold text-green-700 mb-1.5">New Schedule (Rebooking)</p>
                {newFl?.flight_number  && <p><span className="text-gray-500">Flight:</span> <span className="font-mono">{newFl.flight_number}</span></p>}
                {newFl?.departure_time && <p><span className="text-gray-500">Depart:</span> {formatAt(newFl.departure_time)}</p>}
                {newFl?.arrival_time   && <p><span className="text-gray-500">Arrive:</span> {formatAt(newFl.arrival_time)}</p>}
                {newFl?.cabin_class    && <p><span className="text-gray-500">Cabin:</span>  {newFl.cabin_class}</p>}
              </div>
            </div>
          )}

          {/* CRS refs for multi-modal segments */}
          {mutation.crs_refs && Object.keys(mutation.crs_refs).length > 0 && (
            <div>
              <p className="font-semibold text-gray-500 uppercase tracking-wide mb-1.5">CRS/IDS References</p>
              <div className="grid grid-cols-2 gap-3">
                {mutation.crs_refs.hotel && (
                  <div className="bg-purple-50 border border-purple-200 rounded p-2.5">
                    <p className="font-semibold text-purple-700 mb-1">🏨 Hilton CRS</p>
                    <p><span className="text-gray-500">PNR:</span> <span className="font-mono">{mutation.crs_refs.hotel.pnr || '—'}</span></p>
                    {mutation.crs_refs.hotel.property_code && <p><span className="text-gray-500">Property:</span> {mutation.crs_refs.hotel.property_code}</p>}
                    {mutation.crs_refs.hotel.check_in && <p><span className="text-gray-500">Check-in:</span> {mutation.crs_refs.hotel.check_in}</p>}
                  </div>
                )}
                {mutation.crs_refs.car_rental && (
                  <div className="bg-orange-50 border border-orange-200 rounded p-2.5">
                    <p className="font-semibold text-orange-700 mb-1">🚗 National IDS</p>
                    <p><span className="text-gray-500">PNR:</span> <span className="font-mono">{mutation.crs_refs.car_rental.pnr || '—'}</span></p>
                    {mutation.crs_refs.car_rental.pickup_iata && <p><span className="text-gray-500">Pickup:</span> {mutation.crs_refs.car_rental.pickup_iata}</p>}
                    {mutation.crs_refs.car_rental.vehicle_class && <p><span className="text-gray-500">Vehicle:</span> {mutation.crs_refs.car_rental.vehicle_class}</p>}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Links */}
          <div className="flex gap-2 flex-wrap">
            <a
              href={`/admin/cloudtraq_gds/pnr/${mutation.pnr_locator}`}
              className="text-xs px-3 py-1.5 bg-indigo-50 border border-indigo-200 text-indigo-700 rounded hover:bg-indigo-100"
            >
              PNR Detail →
            </a>
            {mutation.itinerary_ref && (
              <a
                href={`/admin/cloudtraq_gds/itinerary/${mutation.itinerary_ref}`}
                className="text-xs px-3 py-1.5 bg-teal-50 border border-teal-200 text-teal-700 rounded hover:bg-teal-100"
              >
                Full Itinerary →
              </a>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function PnrMutations({ mutations, tenant_branding }) {
  const intervalRef = useRef(null)

  useEffect(() => {
    intervalRef.current = setInterval(() => {
      router.reload({ only: ['mutations'] })
    }, 60_000)
    return () => clearInterval(intervalRef.current)
  }, [])

  const totalRevenueDelta = mutations.reduce((n, m) => n + (m.revenue_delta || 0), 0)
  const multiModal = mutations.filter(m => m.itinerary_ref)

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-5xl mx-auto px-4 py-8">

        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">PNR Mutation Log</h1>
            <p className="text-sm text-gray-500 mt-1">
              Rebooking-confirmed GDS/CRS mutations ·{' '}
              {mutations.length} events ·{' '}
              {multiModal.length} multi-modal itineraries
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => router.reload()} className="px-3 py-1.5 text-sm border border-gray-300 rounded hover:bg-gray-100">
              Refresh
            </button>
            <Link href="/admin/cloudtraq_gds" className="text-sm text-blue-600 hover:underline">← Command Center</Link>
          </div>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-3 gap-3 mb-4">
          <div className="bg-white border border-gray-200 rounded-lg p-3 text-center">
            <p className="text-2xl font-bold tabular-nums text-gray-700">{mutations.length}</p>
            <p className="text-xs text-gray-500">Total Mutations</p>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-3 text-center">
            <p className="text-2xl font-bold tabular-nums" style={{ color: multiModal.length > 0 ? '#9C27B0' : '#9E9E9E' }}>
              {multiModal.length}
            </p>
            <p className="text-xs text-gray-500">Multi-Modal Itineraries</p>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-3 text-center">
            <p className="text-2xl font-bold tabular-nums" style={{ color: totalRevenueDelta >= 0 ? '#4CAF50' : '#F44336' }}>
              {totalRevenueDelta >= 0 ? '+' : ''}${Math.abs(totalRevenueDelta).toFixed(2)}
            </p>
            <p className="text-xs text-gray-500">Revenue Delta</p>
          </div>
        </div>

        {/* Mutation list */}
        {mutations.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-lg p-8 text-center">
            <p className="text-gray-400 text-sm">No rebooking mutations recorded yet.</p>
            <p className="text-gray-400 text-xs mt-1">
              Mutations appear when IBM ADK pushes <code>rebooking_confirmed</code> events.
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {mutations.map(m => (
              <MutationCard key={m.pnr_locator + (m.rebooking_at || '')} mutation={m} />
            ))}
          </div>
        )}

        {/* Nav */}
        <div className="mt-6 flex flex-wrap gap-4 text-sm text-gray-500">
          <Link href="/admin/cloudtraq_gds" className="text-blue-600 hover:underline">← Command Center</Link>
          <Link href="/admin/cloudtraq_gds/high_risk" className="text-blue-600 hover:underline">High Risk Candidates</Link>
          <Link href="/admin/cloudtraq_gds/anc_revenue" className="text-blue-600 hover:underline">Anc Revenue</Link>
        </div>
      </div>
    </div>
  )
}
